import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useCart } from "../../context/CartContext";
import toast from "react-hot-toast";

export default function MenuItemCard({ item, restaurantId, restaurantName }) {
  const { cart, addItem, updateQuantity } = useCart();
  const hasSizes = item.sizes && item.sizes.length > 0;

  // Selected size (default to first size if sizes exist)
  const [selectedSize, setSelectedSize] = useState(hasSizes ? item.sizes[0] : null);

  // Cart key includes size so Small and Large are tracked separately
  const cartKey = hasSizes ? `${item.id}-${selectedSize?.name}` : item.id;
  const cartItem = cart.items.find((i) => i.cartKey === cartKey);
  const quantity = cartItem?.quantity || 0;

  const displayPrice = hasSizes ? selectedSize?.price : item.price;
  const differentRestaurant = cart.restaurantId && cart.restaurantId !== restaurantId;

  const handleAdd = () => {
    if (differentRestaurant) {
      if (!window.confirm("Your cart has items from another restaurant. Start a new cart?")) return;
    }
    const cartItem = {
      ...item,
      cartKey,
      price: displayPrice,
      // Show size in name so cart displays correctly
      name: hasSizes ? `${item.name} (${selectedSize?.name})` : item.name,
    };
    addItem(cartItem, restaurantId, restaurantName);
    toast.success(`${cartItem.name} added!`, { duration: 1500 });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex">
        {/* Image */}
        <div className="w-28 h-28 flex-shrink-0 bg-gradient-to-br from-orange-50 to-amber-50">
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl opacity-40">🍛</div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 text-sm leading-tight">{item.name}</h4>
              {item.is_featured && (
                <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full flex-shrink-0">
                  Popular
                </span>
              )}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                {item.description}
              </p>
            )}

            {/* Size selector */}
            {hasSizes && (
              <div className="flex flex-wrap gap-1 mt-2">
                {item.sizes.map((size) => (
                  <button
                    key={size.name}
                    type="button"
                    onClick={() => setSelectedSize(size)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                      selectedSize?.name === size.name
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-gray-200 text-gray-600 hover:border-orange-300"
                    }`}
                  >
                    {size.name} — Rs {size.price}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-2">
            <span className="font-bold text-orange-600">
              Rs {displayPrice?.toLocaleString()}
            </span>

            {!item.is_available ? (
              <span className="text-xs text-gray-400 font-medium">Unavailable</span>
            ) : quantity === 0 ? (
              <button
                onClick={handleAdd}
                className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-all active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateQuantity(cartKey, quantity - 1)}
                  className="w-7 h-7 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 flex items-center justify-center transition-colors"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="font-bold text-gray-900 w-4 text-center text-sm">{quantity}</span>
                <button
                  onClick={handleAdd}
                  className="w-7 h-7 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
